import React from 'react';
import { MapPin, Phone, ArrowRight, CheckCircle, Building2, Clock, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const BaltimoreCityPage = () => {
  const services = [
    { name: 'Outpatient Therapy (OMHC)', desc: 'Individual and group therapy with licensed clinicians for depression, anxiety, trauma, and other mental health conditions.', link: '/services/outpatient-mental-health' },
    { name: 'Psychiatric Rehabilitation (PRP)', desc: 'Community-based skill-building to support independent living, social engagement, and daily functioning.', link: '/services/psychiatric-rehabilitation' },
    { name: 'Medication Management', desc: 'Psychiatric evaluation, prescribing, and ongoing monitoring by experienced providers.', link: '/services/medication-management' },
    { name: 'Case Management', desc: 'Care coordination connecting you to housing, benefits, transportation, and community resources.', link: '/services/case-management' },
    { name: 'Comprehensive Assessment', desc: 'Thorough clinical evaluations to guide diagnosis, treatment planning, and service recommendations.', link: '/services/comprehensive-assessment' },
    { name: 'Telehealth Services', desc: 'Secure virtual sessions for therapy and medication management from the comfort of your home.', link: '/services/telehealth' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Mental Health Services in Baltimore City"
        description="Mental health services in Baltimore City, MD. SDORA Wellbeing provides outpatient therapy, PRP, medication management, and case management near Baltimore. CARF accredited. Accepting Medicaid."
        path="/areas/baltimore-city"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "SDORA Wellbeing LLC — Baltimore City Services",
          "description": "Outpatient mental health therapy, psychiatric rehabilitation, medication management, and case management serving Baltimore City residents.",
          "url": "https://sdorawellbeing.org/areas/baltimore-city",
          "telephone": "+1-877-772-5150",
          "image": "https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png",
          "priceRange": "$$",
          "areaServed": { "@type": "City", "name": "Baltimore", "containedInPlace": { "@type": "State", "name": "Maryland" } },
          "address": { "@type": "PostalAddress", "streetAddress": "1700 Reisterstown Rd, Suite 109", "addressLocality": "Pikesville", "addressRegion": "MD", "postalCode": "21208", "addressCountry": "US" }
        }}
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-800 to-indigo-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Mental Health Services in Baltimore City
          </h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto">
            Compassionate, community-based behavioral health care for Baltimore City residents. Located minutes away in Pikesville with telehealth available statewide.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <section className="mb-12" data-testid="baltimore-city-intro">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Serving the Baltimore City Community</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            SDORA Wellbeing LLC provides outpatient mental health services to adults and youth living in Baltimore City.
            Our office at 1700 Reisterstown Road in Pikesville is conveniently accessible from neighborhoods across the city,
            including Park Heights, Northwest Baltimore, Pimlico, Mondawmin, and surrounding areas.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Whether you are seeking therapy for depression or anxiety, need psychiatric rehabilitation support,
            or require help navigating community resources, our licensed clinicians and case managers are here to help.
            We also offer telehealth for individuals who prefer virtual care.
          </p>
        </section>

        {/* Services Grid */}
        <section className="mb-12" data-testid="baltimore-city-services">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Services Available to Baltimore City Residents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((svc) => (
              <div key={svc.name} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">{svc.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{svc.desc}</p>
                <Link to={svc.link} className="text-sm text-blue-600 font-medium hover:underline inline-flex items-center gap-1">
                  Learn more <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Location & Access */}
        <section className="mb-12 bg-blue-50 rounded-xl p-6 border border-blue-100" data-testid="baltimore-city-location">
          <div className="flex items-start gap-3 mb-4">
            <Building2 className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Our Location</h2>
          </div>
          <div className="space-y-3 text-sm text-gray-700">
            <p><strong>Address:</strong> 1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208</p>
            <p><strong>From Baltimore City:</strong> Approximately 15–20 minutes via Reisterstown Road (MD-140) or Northern Parkway. Accessible by MTA bus routes serving the Reisterstown Road corridor.</p>
            <p><strong>Parking:</strong> Free on-site parking available.</p>
          </div>
        </section>

        {/* Hours */}
        <section className="mb-12" data-testid="baltimore-city-hours">
          <div className="flex items-start gap-3 mb-4">
            <Clock className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Office Hours</h2>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <span className="font-medium">Monday – Friday</span><span>9:00 AM – 5:00 PM</span>
              <span className="font-medium">Saturday – Sunday</span><span>Closed</span>
            </div>
            <p className="text-xs text-gray-500 mt-3">Telehealth appointments may be available outside standard hours. Contact us for scheduling.</p>
          </div>
        </section>

        {/* Conditions We Treat */}
        <section className="mb-12" data-testid="baltimore-city-conditions">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Conditions We Treat</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {[
              'Depression', 'Anxiety Disorders', 'PTSD & Trauma',
              'Bipolar Disorder', 'Schizophrenia', 'ADHD',
              'Anger Management', 'Grief & Loss', 'Adjustment Disorders',
            ].map((condition) => (
              <div key={condition} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{condition}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Insurance */}
        <section className="mb-12 bg-indigo-50 rounded-xl p-6 border border-indigo-100" data-testid="baltimore-city-insurance">
          <div className="flex items-start gap-3 mb-3">
            <Shield className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Insurance & Medicaid</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            SDORA Wellbeing accepts Medicaid, Maryland HealthChoice managed care organizations (Aetna Better Health,
            CareFirst Community, Maryland Physicians Care, Priority Partners, UnitedHealthcare, and others),
            and most major insurance plans. Many Baltimore City residents qualify for Medicaid coverage.
          </p>
          <Link to="/insurance" className="text-sm text-indigo-700 font-semibold hover:underline inline-flex items-center gap-1">
            View all accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Related Resources */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Helpful Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { name: 'Depression Treatment', link: '/resources/depression-treatment' },
              { name: 'Anxiety Treatment', link: '/resources/anxiety-treatment' },
              { name: 'PTSD Support', link: '/resources/ptsd-support' },
            ].map((res) => (
              <Link key={res.name} to={res.link} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors">
                <ArrowRight className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700">{res.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="baltimore-city-cta">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Get Started Today</h2>
          <p className="text-sm text-gray-600 mb-4">
            Baltimore City residents — call us today or submit a referral to connect with compassionate mental health care close to home.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-blue-700 border border-blue-200 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default BaltimoreCityPage;
